/**
 * @fileoverview User documents component for displaying and managing account documents
 */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { intl, fetchData, fetchUrl, HTTP_STATUS, Notifications, getToken } from "dmsbasereact";
import { getBaseURL } from "../../build_config";
import { DmsFileViewerModal } from "../DmsFileViewer";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Grid,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArticleIcon from "@mui/icons-material/Article";
import { DmsInfoCard } from "../DmsDetailModal/DmsInfoCard";
import { DocumentUploadSlotWithExpiration } from "../InstitutionSetupModal/DocumentUploadSlotWithExpiration";
import {
  USER_DOCUMENT_REQUIREMENTS,
  userTypeRequiresDocuments,
} from "../../constants/UserDocumentRequirements";

/**
 * Status chip colors
 */
const STATUS_COLORS = {
  1: "warning", // PENDING_REVIEW
  2: "success", // APPROVED
  3: "error",   // REJECTED
  4: "default", // EXPIRED
};

/**
 * Status icons
 */
const STATUS_ICONS = {
  1: <HourglassEmptyIcon fontSize="small" />,
  2: <CheckCircleIcon fontSize="small" />,
  3: <ErrorIcon fontSize="small" />,
  4: <ErrorIcon fontSize="small" />,
};

/**
 * Component for displaying and uploading user documents
 */
export const UserDocuments = ({ personPk = null,
  allowUpload = true,
  userType = null}) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [uploadingDocType, setUploadingDocType] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);

  // Get required document types for this user type
  const requiredDocTypes = useMemo(() => {
    const type = userType?.toLowerCase();
    return USER_DOCUMENT_REQUIREMENTS[type] || [];
  }, [userType]);

  // Check if user type requires documents
  const showUploadSlots = useMemo(() => {
    return allowUpload && userTypeRequiresDocuments(userType);
  }, [allowUpload, userType]);

  const fetchDocuments = useCallback(async () => {
    if (!personPk) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await fetchData(`/account/admin/persons/${personPk}/documents`, "GET", true);
      if (response.status === HTTP_STATUS.OK_200) {
        setDocuments(response.data?.data || []);
      } else {
        setError(intl.get("FailedToLoadDocuments").d("Failed to load documents"));
      }
    } catch (err) {
      console.error("Failed to fetch documents:", err);
      setError(intl.get("FailedToLoadDocuments").d("Failed to load documents"));
    } finally {
      setLoading(false);
    }
  }, [personPk]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // Fetch document types on mount
  useEffect(() => {
    const fetchDocTypes = async () => {
      try {
        const response = await fetchData("/account/document-types", "GET", true);
        if (response.status === HTTP_STATUS.OK_200) {
          setDocumentTypes(response.data?.data || []);
        }
      } catch (err) {
        console.error("Failed to fetch document types:", err);
      }
    };
    fetchDocTypes();
  }, []);

  const handleDownload = async (filePk, fileName) => {
    try {
      const token = getToken();
      const response = await fetch(`${getBaseURL()}/file/${filePk}`, {
        headers: {
          'Authorization': `Bearer ${token?.access_token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download file:", err);
      Notifications.toast(intl.get("FailedToDownloadFile").d("Failed to download file"), Notifications.TYPE_ERROR);
    }
  };

  const handleViewDocument = (doc) => {
    if (!doc.file_pk) return;
    setSelectedDocument({
      filePk: doc.file_pk,
      fileName: doc.file?.original_filename || doc.document_type_label || intl.get("Document").d("Document"),
    });
    setViewerOpen(true);
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
    setSelectedDocument(null);
  };

  // Handle file selection and immediate upload for a specific document type
  const handleFileSelect = async (docTypeValue, file) => {
    if (!file) return;

    try {
      setUploadingDocType(docTypeValue);
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetchUrl(
        `/account/admin/persons/${personPk}/documents/upload?document_type=${docTypeValue}`,
        'POST',
        true,
        formData,
        false,
        null,
        null,
        null,
        'multipart/form-data',
        false
      );

      if (response.status === HTTP_STATUS.CREATED || response.status === HTTP_STATUS.OK || response.status === HTTP_STATUS.CREATED_201 || response.status === HTTP_STATUS.OK_200) {
        Notifications.toast(intl.get("DocumentUploadedSuccessfully").d("Document uploaded successfully"), Notifications.TYPE_SUCCESS);
        fetchDocuments();
      } else {
        Notifications.toast(response.data?.detail || intl.get("FailedToUploadDocument").d("Failed to upload document"), Notifications.TYPE_ERROR);
      }
    } catch (err) {
      console.error("Failed to upload document:", err);
      Notifications.toast(intl.get("FailedToUploadDocument").d("Failed to upload document"), Notifications.TYPE_ERROR);
    } finally {
      setUploadingDocType(null);
    }
  };

  // Get label for a document type value
  const getDocTypeLabel = (docTypeValue) => {
    const docType = documentTypes.find(dt => dt.value === docTypeValue);
    return docType?.label || `Document ${docTypeValue}`;
  };

  // Get existing document for a document type
  const getExistingDocument = (docTypeValue) => {
    return documents.find(doc => doc.document_type === docTypeValue);
  };

  if (loading) {
    return (
      <DmsInfoCard icon={<ArticleIcon />} title={intl.get("Documents").d("Documents")}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      </DmsInfoCard>
    );
  }

  if (error) {
    return (
      <DmsInfoCard icon={<ArticleIcon />} title={intl.get("Documents").d("Documents")}>
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      </DmsInfoCard>
    );
  }

  return (
    <>
      <DmsInfoCard
        icon={<ArticleIcon />}
        title={intl.get("Documents").d("Documents")}
      >
        {/* Upload slots for required document types - shown only in edit mode */}
        {showUploadSlots && requiredDocTypes.length > 0 && (
          <Grid container spacing={2}>
            {requiredDocTypes.map((docTypeValue) => {
              const existingDoc = getExistingDocument(docTypeValue);
              const isUploading = uploadingDocType === docTypeValue;
              return (
                <Grid item xs={12} sm={6} key={docTypeValue}>
                  <Box sx={{ position: "relative" }}>
                    <DocumentUploadSlotWithExpiration
                      docName={getDocTypeLabel(docTypeValue)}
                      file={null}
                      existingFile={existingDoc?.file ? {
                        pk: existingDoc.file_pk,
                        original_filename: existingDoc.file.original_filename
                      } : null}
                      onFileSelect={(_, file) => handleFileSelect(docTypeValue, file)}
                      disabled={isUploading}
                      requiresExpiration={false}
                    />
                    {isUploading && (
                      <Box sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "rgba(255,255,255,0.7)",
                        borderRadius: 1,
                      }}>
                        <CircularProgress size={24} />
                      </Box>
                    )}
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}

        {/* View mode - show existing documents list */}
        {!showUploadSlots && (
          documents.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {intl.get("NoDocumentsUploaded").d("No documents uploaded")}
            </Typography>
          ) : (
            <List dense>
              {documents.map((doc) => (
                <ListItem
                  key={doc.pk}
                  sx={{
                    bgcolor: "background.paper",
                    borderRadius: 1,
                    mb: 1,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <ListItemIcon>
                    <DescriptionIcon color="action" />
                  </ListItemIcon>
                  <ListItemText
                    primary={doc.document_type_label}
                    secondary={
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        {doc.file?.original_filename && (
                          <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
                            {doc.file.original_filename}
                          </Typography>
                        )}
                        <Chip
                          size="small"
                          label={doc.status_label}
                          color={STATUS_COLORS[doc.status] || "default"}
                          icon={STATUS_ICONS[doc.status]}
                          sx={{ width: "fit-content" }}
                        />
                        {doc.review_notes && (
                          <Typography variant="caption" color="text.secondary">
                            {intl.get("Notes").d("Notes")}: {doc.review_notes}
                          </Typography>
                        )}
                        {doc.created_on && (
                          <Typography variant="caption" color="text.secondary">
                            {intl.get("UploadedOn").d("Uploaded on")}: {new Date(doc.created_on).toLocaleDateString('pt-BR')}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleViewDocument(doc)}
                        title={intl.get("ViewDocument").d("View Document")}
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDownload(doc.file_pk, doc.file?.original_filename)}
                        title={intl.get("Download").d("Download")}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )
        )}
      </DmsInfoCard>

      {/* Document Viewer Modal */}
      <DmsFileViewerModal
        isOpen={viewerOpen}
        onClose={handleCloseViewer}
        filePk={selectedDocument?.filePk}
        fileName={selectedDocument?.fileName}
      />
    </>
  );
};

UserDocuments.propTypes = {
  personPk: PropTypes.number,
  allowUpload: PropTypes.bool,
  userType: PropTypes.string,
};

